import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  amount, 
  description = "Credit Top-up",
  onPaymentSuccess,
  paymentType = "credit-topup",
  user = null, // Add user context
  allowCredits = false, // Allow credits option
  allowFreeRounds = false, // Allow free rounds option
  eventData = null // Event data for free rounds validation
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('payfast');
  
  // Calculate available payment options
  const availableCredits = user?.credits_balance || 0;
  const availableFreeRounds = user?.free_rounds || 0;
  const canUseCredits = allowCredits && availableCredits >= amount;
  const canUseFreeRounds = allowFreeRounds && availableFreeRounds > 0 && eventData?.free_rounds_allowed;
  
  // Debug payment options
  console.log('🔍 PaymentModal: Payment options calculation:');
  console.log('  - User:', user?.email);
  console.log('  - Available credits:', availableCredits);
  console.log('  - Available free rounds:', availableFreeRounds);
  console.log('  - Amount to pay:', amount);
  console.log('  - allowCredits prop:', allowCredits);
  console.log('  - allowFreeRounds prop:', allowFreeRounds);
  console.log('  - eventData:', eventData);
  console.log('  - eventData.free_rounds_allowed:', eventData?.free_rounds_allowed);
  console.log('  - canUseCredits:', canUseCredits);
  console.log('  - canUseFreeRounds:', canUseFreeRounds);
  
  // Auto-select best payment method
  useEffect(() => {
    if (canUseFreeRounds) {
      setPaymentMethod('free_round');
    } else if (canUseCredits) {
      setPaymentMethod('credits');
    } else {
      setPaymentMethod('payfast');
    }
  }, [canUseFreeRounds, canUseCredits]);

  const handlePayment = async () => {
    console.log('🔍 PaymentModal: Starting payment process');
    console.log('🔍 PaymentModal: Payment method:', paymentMethod);
    console.log('🔍 PaymentModal: Amount:', amount);
    console.log('🔍 PaymentModal: Payment type:', paymentType);
    
    setLoading(true);
    
    try {
      let response;
      
      if (paymentMethod === 'credits') {
        // Handle credits payment
        response = await axios.post('/bookings/pay-with-credits', {
          booking_id: paymentType.bookingId,
          amount: amount
        });
        
        toast({
          title: "Payment Successful",
          description: `Paid R${amount.toFixed(2)} with credits`
        });
        
        onPaymentSuccess();
        onClose();
        return;
      }
      
      if (paymentMethod === 'free_round') {
        // Handle free round payment
        response = await axios.post('/bookings/pay-with-free-round', {
          booking_id: paymentType.bookingId
        });
        
        toast({
          title: "Payment Successful", 
          description: "Paid with free round"
        });
        
        onPaymentSuccess();
        onClose();
        return;
      }
      
      if (paymentType === "credit-topup") {
        response = await axios.post('/payment/credit-topup', {
          amount: amount,
          payment_method: paymentMethod
        });
      } else if (paymentType.type === "booking-payment") {
        // Handle booking payment - send as query parameters
        response = await axios.post(`/payment/event-payment?event_id=${paymentType.eventId}&booking_id=${paymentType.bookingId}&amount=${amount}`);
      } else {
        // Legacy support - Handle event payment - send as query parameters
        response = await axios.post(`/payment/event-payment?event_id=${paymentType.eventId}&booking_id=${paymentType.bookingId}&amount=${amount}`);
      }
      
      console.log('🔍 PaymentModal: Payment response:', response.data);
      
      const { payment_form_data, payment_form_action, payment_id } = response.data;
      
      console.log('🔍 PaymentModal: Payment form data:', payment_form_data);
      console.log('🔍 PaymentModal: Payment form action:', payment_form_action);
      
      // Store payment ID for tracking
      localStorage.setItem('pending_payment_id', payment_id);
      
      // Create and submit POST form to PayFast
      console.log('🔍 PaymentModal: Creating POST form for PayFast...');
      console.log('🔍 PaymentModal: Form action URL:', payment_form_action);
      console.log('🔍 PaymentModal: Form data keys:', Object.keys(payment_form_data));
      
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payment_form_action;
      form.style.display = 'none';
      
      // Add all payment data as hidden form fields
      Object.keys(payment_form_data).forEach(key => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = payment_form_data[key];
        form.appendChild(input);
        console.log(`🔍 PaymentModal: Added form field: ${key} = ${payment_form_data[key]}`);
      });
      
      // Submit form
      document.body.appendChild(form);
      console.log('🔍 PaymentModal: Form created and added to DOM, submitting...');
      
      // Close the modal immediately when redirecting to PayFast
      // Do NOT call onPaymentSuccess here - that happens on return from PayFast
      onClose();
      
      form.submit();
      console.log('🔍 PaymentModal: Form submitted to PayFast! Modal closed.');
      
    } catch (error) {
      console.error('Payment initiation error:', error);
      toast({
        variant: "destructive",
        title: "Payment Failed",
        description: error.response?.data?.detail || "Failed to initiate payment"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Payment</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Payment Summary */}
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Item</span>
                <span className="text-sm text-gray-900">{description}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                <span className="text-lg font-bold text-emerald-600">R{amount?.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Payment Method Selection */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-3 block">
              Payment Method
            </Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {canUseFreeRounds && (
                  <SelectItem value="free_round">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center space-x-2">
                        <span>🎟️</span>
                        <span>Free Round</span>
                      </div>
                      <Badge className="bg-purple-100 text-purple-800 ml-2">
                        {availableFreeRounds} available
                      </Badge>
                    </div>
                  </SelectItem>
                )}
                {canUseCredits && (
                  <SelectItem value="credits">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center space-x-2">
                        <span>💰</span>
                        <span>Credits</span>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-800 ml-2">
                        R{availableCredits.toFixed(2)} available
                      </Badge>
                    </div>
                  </SelectItem>
                )}
                <SelectItem value="payfast">
                  <div className="flex items-center space-x-2">
                    <span>💳</span>
                    <span>PayFast (Cards, EFT, Instant EFT)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Payment Method Info - Changes based on selection */}
          {paymentMethod === 'payfast' && (
            <Alert>
              <AlertDescription>
                You will be redirected to PayFast's secure payment page to complete your transaction.
                PayFast accepts all major credit cards, debit cards, and bank transfers.
              </AlertDescription>
            </Alert>
          )}
          
          {paymentMethod === 'credits' && (
            <Alert className="border-emerald-200 bg-emerald-50">
              <AlertDescription className="text-emerald-800">
                <strong>💰 Pay with Credits</strong><br />
                R{amount?.toFixed(2)} will be deducted from your credit balance.
                Remaining balance after payment: R{(availableCredits - (amount || 0)).toFixed(2)}
              </AlertDescription>
            </Alert>
          )}
          
          {paymentMethod === 'free_round' && (
            <Alert className="border-purple-200 bg-purple-50">
              <AlertDescription className="text-purple-800">
                <strong>🎟️ Use Free Round</strong><br />
                1 free round will be used for this booking.
                Remaining free rounds after use: {availableFreeRounds - 1}
              </AlertDescription>
            </Alert>
          )}

          {/* Security Notice - Only show for PayFast */}
          {paymentMethod === 'payfast' && (
            <div className="text-xs text-gray-500 text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <span>🔒</span>
                <span>Secure payment powered by PayFast</span>
              </div>
              <div>Your payment information is encrypted and secure</div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              className={`flex-1 ${
                paymentMethod === 'free_round' 
                  ? 'bg-purple-600 hover:bg-purple-700' 
                  : paymentMethod === 'credits'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
              disabled={loading || !amount}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </div>
              ) : paymentMethod === 'free_round' ? (
                '🎟️ Use Free Round'
              ) : paymentMethod === 'credits' ? (
                `💰 Pay with Credits`
              ) : (
                `💳 Pay R${amount?.toFixed(2)}`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;